import React from 'react'
import './detail-content.scss'
import { useDispatch } from 'react-redux'
import { refetchApplicationCalculationData } from 'api/applications'
import type { AppDispatch } from 'store'
import ApplicationSummaryMeta from './components/summary-meta'
import ApplicationClientSections from './components/client-sections'
import ApplicationManagerPanel from './components/manager-panel'
import { buildProposalDraftBody } from './proposal-service-line-utils'

const ApplicationDetailContent = ({ data }: ApplicationDetailContentProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    app,
    managerView,
    checkStatuses,
    runAction,
    answerClarification,
    publicId,
    requestTransfer,
    requestContacts,
    declineProposal,
    outcomes,
    companyOptions,
    executorId,
    setExecutorId,
    executorChangeReason,
    setExecutorChangeReason,
    proposalForm,
    setProposalForm,
    contactForm,
    setContactForm,
    clientContactForm,
    setClientContactForm,
    outcome,
    setOutcome,
    outcomeNote,
    setOutcomeNote,
    assignManager,
    isTakingWork,
    assignExecutor,
    updateCheckItem,
    confirmCheckItemTnvedChanges,
    postCheckItemComment,
    postAdminCheckItemComment,
    uploadCheckItemDocument,
    deleteCheckItemDocument,
    completePreparation,
    saveProposal,
    publishProposal,
    revealContacts,
    saveClientContacts,
    saveExecutorContacts,
    updateOutcome,
  } = data

  const managerPanel: ApplicationManagerPanelData = {
    app: app,
    managerView: managerView,
    isTakingWork,
    checkStatuses: checkStatuses,
    outcomes: outcomes,
    companyOptions: companyOptions,
    executorId: executorId,
    onExecutorIdChange: setExecutorId,
    executorChangeReason: executorChangeReason,
    onExecutorChangeReasonChange: setExecutorChangeReason,
    proposalForm: proposalForm,
    onProposalFormChange: setProposalForm,
    contactForm: contactForm,
    onContactFormChange: setContactForm,
    clientContactForm: clientContactForm,
    onClientContactFormChange: setClientContactForm,
    onSaveClientContacts: () => runAction(() => saveClientContacts({
      publicId,
      ...clientContactForm,
    }).unwrap(), 'applications__message__clientContactsSaved'),
    onSaveExecutorContacts: () => runAction(() => saveExecutorContacts({
      publicId,
      ...contactForm,
    }).unwrap(), 'applications__message__executorContactsSaved'),
    outcome: outcome,
    onOutcomeChange: setOutcome,
    outcomeNote: outcomeNote,
    onOutcomeNoteChange: setOutcomeNote,
    runAction: runAction,
    onTakeWork: () => assignManager(publicId).unwrap(),
    onAssignExecutor: (overrides) => assignExecutor({
      publicId,
      executor_company_id: overrides?.executor_company_id ?? Number(executorId),
      ...((overrides?.change_reason ?? executorChangeReason).trim()
        ? { change_reason: (overrides?.change_reason ?? executorChangeReason).trim() }
        : {}),
    }).unwrap(),
    onUpdateCheckItem: ({ kind, body }) => updateCheckItem({
      publicId,
      kind,
      ...body,
    }).unwrap(),
    onPostCheckItemComment: ({ kind, body, attachments }) => (
      managerView
        ? postAdminCheckItemComment({ publicId, kind, body, attachments })
        : postCheckItemComment({ publicId, kind, body, attachments })
    ).unwrap(),
    onConfirmTnvedChanges: async () => {
      await confirmCheckItemTnvedChanges({ publicId }).unwrap()
      refetchApplicationCalculationData(dispatch, app.calculation_number)
    },
    onUploadCheckItemDocument: ({ kind, file }) => uploadCheckItemDocument({
      publicId,
      kind,
      file,
    }).unwrap(),
    onDeleteCheckItemDocument: ({ kind, fileId }) => deleteCheckItemDocument({
      publicId,
      kind,
      fileId,
    }).unwrap(),
    onCompletePreparation: () => completePreparation(publicId).unwrap(),
    onSaveProposal: () => saveProposal({
      publicId,
      body: buildProposalDraftBody(proposalForm, app),
    }).unwrap(),
    onPublishProposal: async () => {
      await saveProposal({
        publicId,
        body: buildProposalDraftBody(proposalForm, app),
      }).unwrap()
      return publishProposal(publicId).unwrap()
    },
    onRevealContacts: () => runAction(() => revealContacts({ publicId, ...contactForm }).unwrap(), 'applications__message__contactsShared'),
    onUpdateOutcome: () => updateOutcome({
      publicId,
      outcome,
      outcome_note: outcomeNote,
    }).unwrap(),
  }

  return (
    <article className='application-detail-content'>
      <ApplicationSummaryMeta app={app} managerView={managerView} />

      <ApplicationClientSections
        app={app}
        managerView={managerView}
        checkStatuses={checkStatuses}
        runAction={runAction}
        onAnswer={({ clarificationId, answer_text, attachments }) => answerClarification({
          publicId,
          clarificationId,
          answer_text,
          attachments,
        }).unwrap()}
        onPostCheckItemComment={managerPanel.onPostCheckItemComment}
        onConfirmTnvedChanges={managerPanel.onConfirmTnvedChanges}
        onRequestTransfer={() => requestTransfer(publicId).unwrap()}
        onAcceptProposal={(body) => requestContacts({ publicId, ...body }).unwrap()}
        onDeclineProposal={(reason) => declineProposal({
          publicId,
          reason,
        }).unwrap()}
      />

      <ApplicationManagerPanel panel={managerPanel} />
    </article>
  )
}

export default ApplicationDetailContent
